import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";

const PRIMARY_COLOR = "#4200ff";
const SECONDARY_COLOR = "#ff02c7";
const TEXT_COLOR = "#8ba4bf";

const styles = StyleSheet.create({
  page: { padding: 30, fontSize: 12, fontFamily: "Helvetica", color: TEXT_COLOR },
  header: { marginBottom: 10 },
  headerTitle: { color: PRIMARY_COLOR, fontSize: 16, fontWeight: "bold" },
  metadata: { marginBottom: 20 },
  row: { flexDirection: "row", justifyContent: "space-between", marginBottom: 20 },
  table: {
    display: "table",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: PRIMARY_COLOR,
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  tableRow: { flexDirection: "row" },
  tableColHeader: {
    width: "17%",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: PRIMARY_COLOR,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: PRIMARY_COLOR,
  },
  tableCol: {
    width: "17%",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: PRIMARY_COLOR,
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
  cellHeader: { margin: 5, fontSize: 10, fontWeight: "bold", color: "white" },
  cell: { margin: 5, fontSize: 10 },
  footer: { marginTop: 30, textAlign: "center" },
});

const VAT_RATE = 21; // Default VAT rate in Belgium

const InvoicePDF = ({ invoice, organization }) => {
  const products = invoice?.products || [];
  const totals = products.reduce(
    (acc, p) => {
      const rate = Number(p.vatRate) || VAT_RATE;
      const quantity = Number(p.quantity || 0);
      const unitIncl = Number(p.price);
      const unitExcl = unitIncl / (1 + rate / 100);
      acc.excl += unitExcl * quantity;
      acc.vat += (unitIncl - unitExcl) * quantity;
      return acc;
    },
    { excl: 0, vat: 0 },
  );
  const totalExcl = totals.excl;
  const totalVat = totals.vat;
  const totalIncl = totalExcl + totalVat;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>
            {organization?.legalName || organization?.organization}
          </Text>
          {(organization?.companyNumber || organization?.vatNumber) && (
            <Text>
              {organization?.companyNumber ? `Company No: ${organization.companyNumber}` : ""}
              {organization?.companyNumber && organization?.vatNumber ? " - " : ""}
              {organization?.vatNumber ? `VAT: ${organization.vatNumber}` : ""}
            </Text>
          )}
          {organization?.businessAddress ? (
            <Text>{organization.businessAddress}</Text>
          ) : (
            organization?.address && <Text>{organization.address}</Text>
          )}
        </View>

        <View style={styles.metadata}>
          {(invoice?.invoiceNumber || invoice?.id) && (
            <Text>Invoice #{invoice?.invoiceNumber || invoice?.id}</Text>
          )}
          {invoice?.createdAt && (
            <Text>Date: {new Date(Number(invoice.createdAt)).toLocaleDateString()}</Text>
          )}
        </View>

        <View style={styles.row}>
          <View>
            <Text>Bill To:</Text>
            {invoice?.customerCompanyName && <Text>{invoice.customerCompanyName}</Text>}
            {invoice?.customerInvoiceAddress && <Text>{invoice.customerInvoiceAddress}</Text>}
            {invoice?.customerVatNumber && <Text>VAT: {invoice.customerVatNumber}</Text>}
          </View>
        </View>

        <View style={styles.table}>
          <View style={styles.tableRow}>
            <View style={styles.tableColHeader}>
              <Text style={styles.cellHeader}>Qty</Text>
            </View>
            <View style={styles.tableColHeader}>
              <Text style={styles.cellHeader}>Description</Text>
            </View>
            <View style={styles.tableColHeader}>
              <Text style={styles.cellHeader}>Unit Price</Text>
            </View>
            <View style={styles.tableColHeader}>
              <Text style={styles.cellHeader}>VAT %</Text>
            </View>
            <View style={styles.tableColHeader}>
              <Text style={styles.cellHeader}>Excl. VAT</Text>
            </View>
            <View style={styles.tableColHeader}>
              <Text style={styles.cellHeader}>Line Total</Text>
            </View>
          </View>
          {invoice?.products?.map((p, idx) => {
            const rate = Number(p.vatRate) || VAT_RATE;
            const quantity = Number(p.quantity || 0);
            const unitIncl = Number(p.price);
            const unitExcl = unitIncl / (1 + rate / 100);
            const lineExcl = unitExcl * quantity;
            const lineTotal = unitIncl * quantity;
            return (
              <View style={styles.tableRow} key={idx}>
                <View style={styles.tableCol}>
                  <Text style={styles.cell}>{p.quantity}</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.cell}>{p.name}</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.cell}>{unitIncl.toFixed(2)}€</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.cell}>{rate}%</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.cell}>{lineExcl.toFixed(2)}€</Text>
                </View>
                <View style={styles.tableCol}>
                  <Text style={styles.cell}>{lineTotal.toFixed(2)}€</Text>
                </View>
              </View>
            );
          })}
        </View>

        <View style={{ marginTop: 10, alignSelf: "flex-end" }}>
          <Text style={{ color: SECONDARY_COLOR, fontWeight: "bold" }}>
            Total excl. VAT: {totalExcl.toFixed(2)}€
          </Text>
          <Text style={{ color: SECONDARY_COLOR, fontWeight: "bold" }}>
            VAT ({VAT_RATE}%): {totalVat.toFixed(2)}€
          </Text>
          <Text style={{ color: SECONDARY_COLOR, fontWeight: "bold" }}>
            Total incl. VAT: {totalIncl.toFixed(2)}€
          </Text>
        </View>

        <View style={{ marginTop: 20 }}>
          {invoice?.paid ? (
            invoice?.paymentMethod && (
              <Text>
                Paid{" "}
                {invoice.paymentMethod === "cash" ? "in cash" : `with ${invoice.paymentMethod}`}
              </Text>
            )
          ) : (
            <>
              {organization?.bankAccount && <Text>IBAN: {organization.bankAccount}</Text>}
              {/* {organization?.bic && <Text>BIC: {organization.bic}</Text>} */}
              {(invoice?.invoiceNumber || invoice?.id) && (
                <Text>Payment reference: {invoice?.invoiceNumber || invoice?.id}</Text>
              )}
              <Text>Payment terms: Due in 7 days</Text>
            </>
          )}
        </View>

        <View style={styles.footer}>
          <Text>
            Document generated by Comitty in the name of its customer{" "}
            {organization?.legalName || organization?.organization}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default InvoicePDF;
